import _ from 'underscore';

import {assert} from 'Asserts';
import {Batch} from 'constants/SampleConstants';
import {BioentitySummary, SchemaTag, TargetSample} from 'folder-items/FileConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {
  PreparedSample,
  PreparedSampleGroup,
  PreparedSampleTag,
  PreparedUISampleGroup,
} from 'workflows/samples/PreparedSampleGroupConstants';
import {BaseSampleGroup, SampleGroup, StageRunSample} from 'workflows/WorkflowConstants';

import makeSample from './makeSample';

// A collection of lookup tables from various stores that are needed to build a PreparedSample
export interface PreparedSampleDataLookups {
  batchesById: {[id: string]: Batch};
  fileSummariesById: {[id: string]: BioentitySummary};
  stageRunSamplesById: {[id: string]: StageRunSample};
}

function _getSampleBatchId(sample: StageRunSample): string | null {
  return sample.batch ? sample.batch.id : null;
}

function getDisplayInfo(
  lookups: PreparedSampleDataLookups,
  sample: StageRunSample
): {name: string; url: string | null} {
  const defaultDisplayInfo = {name: sample.name, url: sample.url};
  const batchId = _getSampleBatchId(sample);
  if (sample.fileId) {
    const fileSummary = lookups.fileSummariesById[sample.fileId];

    assert(fileSummary != null, 'Expected file to exist in lookup map', {
      fileId: sample.fileId,
      fileSummariesById: lookups.fileSummariesById,
    });
    return fileSummary != null ? {name: fileSummary.name, url: fileSummary.editURL} : defaultDisplayInfo;
  } else if (batchId) {
    const batch = lookups.batchesById[batchId];
    assert(batch != null, 'Expected batch to exist in lookup map', {
      batchId,
      batchesById: lookups.batchesById,
    });
    return batch != null ? {name: batch.name, url: batch.url} : defaultDisplayInfo;
  } else {
    return defaultDisplayInfo;
  }
}

function prepareTargetSamples(
  lookups: PreparedSampleDataLookups,
  targetSamples: Array<TargetSample>
): Array<PreparedSample> {
  return targetSamples.map(({id: sampleId, name: targetName, url: targetUrl}) => {
    // Fall back to the server-provided name/url when the stage-run-sample lookup is missing.
    const sample = sampleId != null ? lookups.stageRunSamplesById[sampleId] : undefined;
    if (sample == null) {
      return makeSample({}, sampleId, targetName, targetUrl);
    }
    const {containerId, fileId} = sample;
    const {name, url} = getDisplayInfo(lookups, sample);
    return makeSample({batch: sample.batch, containerId, fileId}, sampleId, name, url);
  });
}

function prepareTags(lookups: PreparedSampleDataLookups, tags: Array<SchemaTag>): Array<PreparedSampleTag> {
  return tags.map((tag) => ({
    ...tag,
    targetSamples: prepareTargetSamples(lookups, tag.targetSamples!),
  }));
}

export function prepareSampleGroups(
  state: BenchlingState,
  sampleGroupIds: Array<string>,
  {batchesById}: {batchesById: {[batchId: string]: Batch}}
): {[sampleGroupId: string]: PreparedSampleGroup | PreparedUISampleGroup} {
  const stageRunSamplesById = new WorkflowSelector(state.models.workflows).getStageRunSampleById();
  const sampleGroups: Array<SampleGroup> = Object.values(state.models.sampleGroups.sampleGroupsById).filter(
    (sampleGroup) => sampleGroupIds.includes(sampleGroup.id)
  );
  const {fileSummariesById} = state.models;
  const preparedSampleGroups = prepareSampleGroupsForTable(
    {stageRunSamplesById, batchesById, fileSummariesById},
    sampleGroups
  );
  return _.indexBy(preparedSampleGroups, 'id');
}

/**
 * Prepare an array of SampleGroup or UISampleGroup to their corresponding PreparedSampleGroup or
 * PreparedUISampleGroup
 *
 * Note: ideally the return value type should be another generic based on a base PreparedSampleGroup but TS
 * does not properly type-check that.
 */
export default function prepareSampleGroupsForTable<T extends BaseSampleGroup>(
  lookups: PreparedSampleDataLookups,
  sampleGroups: Array<T>
): Array<T & {tags: Array<PreparedSampleTag>}> {
  return sampleGroups.map((sampleGroup) => ({
    ...sampleGroup,
    tags: prepareTags(lookups, sampleGroup.tags),
  }));
}
